// Import QRCode library from npm
import QRCode from 'qrcode';

/**
 * Modal for configuring QR code information
 */
export default class QRCodeModal {
    /**
     * Create a new QR code modal
     */
    constructor() {
        this.isVisible = false;
        this.qrData = {
            qrContent: '',
            qrSize: 'medium',
            qrColor: '#000000'
        };
        this.createModal();
        this.bindEvents();
    }

    /**
     * Create the modal DOM structure
     */
    createModal() {
        this.modal = document.createElement('div');
        this.modal.className = 'stc-qrcode-modal';
        
        this.modal.innerHTML = `
            <div class="stc-qrcode-modal-content">
                <div class="stc-qrcode-modal-header">
                    <h3>Create QR Code</h3>
                    <button class="stc-qrcode-modal-close">&times;</button>
                </div>
                <div class="stc-qrcode-modal-body">
                    <div class="stc-qrcode-form">
                        <div class="stc-qrcode-input-group">
                            <label for="qrcode-content">QR Code Content</label>
                            <textarea id="qrcode-content" class="stc-qrcode-textarea" 
                                placeholder="Enter URL or text (e.g., https://example.com)" rows="3"></textarea>
                            <div class="stc-qrcode-helper">
                                Enter a website URL, contact information, or any text that should be encoded in the QR code.
                            </div>
                        </div>
                        
                        <div class="stc-qrcode-row">
                            <div class="stc-qrcode-input-group stc-qrcode-input-half">
                                <label for="qrcode-size">QR Code Size</label>
                                <select id="qrcode-size" class="stc-qrcode-select">
                                    <option value="small">Small</option>
                                    <option value="medium" selected>Medium</option>
                                    <option value="large">Large</option>
                                </select>
                            </div>
                            
                            <div class="stc-qrcode-input-group stc-qrcode-input-half">
                                <label for="qrcode-color">QR Code Color</label>
                                <input type="color" id="qrcode-color" class="stc-qrcode-color" value="#000000">
                            </div>
                        </div>
                    </div>
                    
                    <div class="stc-qrcode-preview">
                        <div class="stc-qrcode-preview-text">Preview</div>
                        <div class="stc-qrcode-preview-container">
                            <div class="stc-qrcode-preview-content">
                                <div id="qrcode-preview-image" class="stc-qrcode-preview-image stc-qrcode-size-medium"></div>
                                <div id="qrcode-preview-text"></div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="stc-qrcode-modal-footer">
                    <button class="stc-qrcode-cancel-btn">Cancel</button>
                    <button class="stc-qrcode-apply-btn" disabled>Apply</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(this.modal);
    }
    
    /**
     * Bind event listeners
     */
    bindEvents() {
        // Modal close button
        this.modal.querySelector('.stc-qrcode-modal-close').addEventListener('click', () => {
            this.hide();
        });
        
        // Cancel button
        this.modal.querySelector('.stc-qrcode-cancel-btn').addEventListener('click', () => {
            this.hide();
        });
        
        // Apply button
        this.modal.querySelector('.stc-qrcode-apply-btn').addEventListener('click', () => {
            this.saveData();
            this.hide();
        });
        
        // QR code content input
        const contentInput = this.modal.querySelector('#qrcode-content');
        if (contentInput) {
            contentInput.addEventListener('input', () => {
                this.qrData.qrContent = contentInput.value;
                this.updatePreview();
                
                // Enable/disable apply button based on content
                const applyBtn = this.modal.querySelector('.stc-qrcode-apply-btn');
                if (applyBtn) {
                    applyBtn.disabled = !this.qrData.qrContent.trim();
                }
            });
        }
        
        // QR code size selection
        const sizeSelect = this.modal.querySelector('#qrcode-size');
        if (sizeSelect) {
            sizeSelect.addEventListener('change', () => {
                this.qrData.qrSize = sizeSelect.value;
                
                // Update the preview container class for size
                const previewImage = this.modal.querySelector('#qrcode-preview-image');
                if (previewImage) {
                    previewImage.className = `stc-qrcode-preview-image stc-qrcode-size-${this.qrData.qrSize}`;
                }
                
                this.updatePreview();
            });
        }
        
        // QR code color picker
        const colorInput = this.modal.querySelector('#qrcode-color');
        if (colorInput) {
            colorInput.addEventListener('input', () => {
                this.qrData.qrColor = colorInput.value;
                this.updatePreview();
            });
        }
        
        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isVisible) {
                this.hide();
            }
        });
    }
    
    /**
     * Update preview with current data
     */
    async updatePreview() {
        const previewImage = this.modal.querySelector('#qrcode-preview-image');
        const previewText = this.modal.querySelector('#qrcode-preview-text');
        
        if (!previewImage) return;
        
        // Clear previous content
        previewImage.innerHTML = '';
        
        if (!this.qrData.qrContent.trim()) {
            previewImage.innerHTML = '<div class="stc-qrcode-placeholder">Enter content to generate QR code</div>';
            if (previewText) {
                previewText.textContent = '';
            }
            return;
        }
        
        try {
            // Generate QR code using the imported library
            const size = this.getSizePixels();
            const color = this.qrData.qrColor;
            
            // Create a QR code data URL with color
            const qrDataUrl = await QRCode.toDataURL(this.qrData.qrContent, {
                width: size,
                margin: 1,
                color: {
                    dark: color,
                    light: '#ffffff'
                },
                errorCorrectionLevel: 'H'
            });
            
            // Store the data URL in our qrData object
            this.qrData.dataUrl = qrDataUrl;
            
            // Create and add the image to the preview
            const img = document.createElement('img');
            img.src = qrDataUrl;
            img.alt = 'QR Code';
            img.style.width = '100%';
            img.style.height = 'auto';
            
            previewImage.innerHTML = '';
            previewImage.appendChild(img);
            
        } catch (error) {
            console.error('Error generating QR code:', error);
            previewImage.innerHTML = '<div class="stc-qrcode-placeholder">Error generating QR code</div>';
        }
        
        if (previewText) {
            previewText.textContent = this.qrData.qrContent;
        }
    }
    
    /**
     * Get pixel size based on selected size
     */
    getSizePixels() {
        switch(this.qrData.qrSize) {
            case 'small': return 128;
            case 'large': return 256;
            case 'medium':
            default: return 192;
        }
    }
    
    /**
     * Set initial form data
     * 
     * @param {object} data - Initial QR code data
     */
    setInitialData(data) {
        this.qrData = {
            qrContent: data.qrContent || '',
            qrSize: data.qrSize || 'medium',
            qrColor: data.qrColor || '#000000',
            dataUrl: data.dataUrl || null
        };
        
        // Set form values
        const contentInput = this.modal.querySelector('#qrcode-content');
        const sizeSelect = this.modal.querySelector('#qrcode-size');
        const colorInput = this.modal.querySelector('#qrcode-color');
        
        if (contentInput) contentInput.value = this.qrData.qrContent;
        if (sizeSelect) sizeSelect.value = this.qrData.qrSize;
        if (colorInput) colorInput.value = this.qrData.qrColor;
        
        // Enable/disable apply button based on content
        const applyBtn = this.modal.querySelector('.stc-qrcode-apply-btn');
        if (applyBtn) {
            applyBtn.disabled = !this.qrData.qrContent.trim();
        }
        
        this.updatePreview();
    }
    
    /**
     * Save data and notify listeners
     */
    saveData() {
        this.emit('save', this.qrData);
    }
    
    /**
     * Show the modal
     */
    show() {
        this.isVisible = true;
        this.modal.classList.add('is-active');
        this.emit('show');
    }
    
    /**
     * Hide the modal
     */
    hide() {
        this.isVisible = false;
        this.modal.classList.remove('is-active');
        this.emit('hide');
    }
    
    /**
     * Emit an event
     * 
     * @param {string} eventName - Event name
     * @param {object} detail - Event detail
     */
    emit(eventName, detail = {}) {
        const event = new CustomEvent(`stc:qrcode:${eventName}`, { detail });
        this.modal.dispatchEvent(event);    
    }

    /**
     * Listen for event
     * 
     * @param {string} eventName - Event name
     * @param {function} callback - Event callback
     */
    on(eventName, callback) {
        this.modal.addEventListener(`stc:qrcode:${eventName}`, callback);
    }

    /**
     * Remove event listener
     * 
     * @param {string} eventName - Event name
     * @param {function} callback - Event callback
     */
    off(eventName, callback) {
        this.modal.removeEventListener(`stc:qrcode:${eventName}`, callback);
    }
}